import React, { FC, useCallback, useMemo } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { ProductsSequenceProduct } from 'common/contracts';
import { EProductType } from 'constants/enums';
import { roundedHoursOptions } from 'utils/selectOptionsUtils';
import { OfferDesignSelector } from 'views/Offers/components/OfferDesignSelector';
import { SegmentsSection } from 'views/Offers/components/sections';
import { IOfferDesignOption } from 'views/Offers/hooks/useOfferDesignOptions';
import { ProductEntry } from 'views/Offers/Popups/Form/hooks/usePopupProducts';

import FormSectionsTabs from 'components/FormSectionsTabs';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import RichText from 'components/RichText/RichText';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from 'components/ui';
import { Separator } from 'components/ui/separator';
import { Tabs, TabsContent } from 'components/ui/tabs';

import { ProductsSection } from '../../../Popups/Form/components/Sections/ProductsSection';
import { getPriorityOptions, PRIORITY_NONE_VALUE } from '../../../Popups/Form/PopupsForm.consts';
import {
  REWARD_CALENDAR_TIMEZONE_OPTIONS,
  RewardCalendarFormFields,
  RewardCalendarTimeZone
} from '../RewardCalendarForm.consts';

interface IRewardCalendarContentProps {
  form: UseFormReturn<RewardCalendarFormFields>;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  offerDesignOptions: IOfferDesignOption[];
  isNameTaken: boolean;
  isPublisherOfferIdTaken: boolean;
  getProducts: UseQueryResult;
  productsList: ProductEntry[];
  addProduct: (product: ProductsSequenceProduct) => void;
  deleteProduct: (id: string) => void;
  updateProduct: (id: string, product: ProductsSequenceProduct) => void;
  formatProductQuantity: UseMutationResult<
    unknown,
    unknown,
    { quantity: string; productType: EProductType },
    unknown
  >;
}

const formatHourValue = (date: Date | undefined): string => {
  if (!date) {
    return '';
  }
  const hours = date.getHours().toString().padStart(2, '0');
  return `${hours}:00`;
};

const RewardCalendarContent: FC<IRewardCalendarContentProps> = ({
  form,
  activeTab,
  setActiveTab,
  offerDesignOptions,
  isNameTaken,
  isPublisherOfferIdTaken,
  getProducts,
  productsList,
  addProduct,
  deleteProduct,
  updateProduct,
  formatProductQuantity
}): React.ReactElement => {
  const priorityOptions = useMemo(() => getPriorityOptions(), []);

  const nameMessage = useMemo((): React.ReactNode => {
    if (isNameTaken) {
      return <FormMessage>Name already exists</FormMessage>;
    }
    return <FormMessage />;
  }, [isNameTaken]);

  const externalIdMessage = useMemo((): React.ReactNode => {
    if (isPublisherOfferIdTaken) {
      return <FormMessage>External ID already exists</FormMessage>;
    }
    return <FormMessage />;
  }, [isPublisherOfferIdTaken]);

  const renderNameField = useCallback(
    ({ field }: { field: any }): React.ReactElement => (
      <FormItem>
        <FormControl>
          <Input
            isRequired
            label="Name"
            placeholder="Name"
            {...field}
          />
        </FormControl>
        {nameMessage}
      </FormItem>
    ),
    [nameMessage]
  );

  const renderExternalIdField = useCallback(
    ({ field }: { field: any }): React.ReactElement => (
      <FormItem>
        <FormControl>
          <Input
            isRequired
            label="External ID"
            placeholder="External ID"
            {...field}
          />
        </FormControl>
        {externalIdMessage}
      </FormItem>
    ),
    [externalIdMessage]
  );

  const handleOfferDesignChange = useCallback(
    (value: string): void => {
      form.setValue('offerUiId', value, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [form]
  );

  const renderOfferDesignField = useCallback(
    ({ field: { value } }: { field: { value: string } }): React.ReactElement => (
      <FormItem>
        <FormControl>
          <OfferDesignSelector
            options={offerDesignOptions}
            selectedValue={value}
            onChange={handleOfferDesignChange}
            listDataTestId="rewardCalendarOfferDesignList"
            placeholder="Select Offer Design"
          />
        </FormControl>
        <FormMessage />
      </FormItem>
    ),
    [offerDesignOptions, handleOfferDesignChange]
  );

  const renderPriorityField = useCallback(
    ({
      field: { value, onChange }
    }: {
      field: { value: number; onChange: (...event: unknown[]) => void };
    }): React.ReactElement => {
      const selectedPriority = priorityOptions.find(
        (option) => option.value === value
      );
      return (
        <FormItem>
          <FormControl>
            <InputSelectDropdown
              key={String(value)}
              label="Priority"
              placeholder="Enter Number"
              isRequired
              defaultSelected={selectedPriority}
              options={priorityOptions.filter(
                (option) => option.value !== PRIORITY_NONE_VALUE
              )}
              onOptionChange={onChange}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      );
    },
    [priorityOptions]
  );

  const renderTimeZoneField = useCallback(
    ({
      field: { value, onChange }
    }: {
      field: {
        value: RewardCalendarTimeZone | undefined;
        onChange: (val: RewardCalendarTimeZone) => void;
      };
    }): React.ReactElement => {
      const selected = REWARD_CALENDAR_TIMEZONE_OPTIONS.find(
        (option) => option.value === value
      );
      return (
        <FormItem>
          <FormControl>
            <InputSelectDropdown
              key={value ?? 'tz'}
              label="Time Zone"
              placeholder="Select Time Zone"
              isRequired
              options={REWARD_CALENDAR_TIMEZONE_OPTIONS}
              defaultSelected={selected}
              onOptionChange={(next) =>
                onChange(next as RewardCalendarTimeZone)
              }
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      );
    },
    []
  );

  const renderStartTimeField = useCallback(
    ({
      field: { value, onChange }
    }: {
      field: { value: Date | undefined; onChange: (val: Date) => void };
    }): React.ReactElement => {
      const currentHourValue = formatHourValue(value);
      const selected = roundedHoursOptions.find(
        (option) => option.value === currentHourValue
      );
      const handleHourChange = (next: string | number): void => {
        const hour = parseInt(String(next).split(':')[0], 10);
        const baseDate = value ? new Date(value) : new Date();
        baseDate.setHours(hour, 0, 0, 0);
        onChange(baseDate);
      };
      return (
        <FormItem>
          <FormControl>
            <InputSelectDropdown
              key={currentHourValue || 'start-time'}
              label="Start Time"
              placeholder="Select Hour"
              isRequired
              options={roundedHoursOptions}
              defaultSelected={selected}
              onOptionChange={handleHourChange}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      );
    },
    []
  );

  const handleSegmentsChange = useCallback(
    (segments: string[]): void => {
      form.setValue('segments', segments, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [form]
  );

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <FormSectionsTabs
        currentTab={activeTab}
        tabs={[
          { value: 'settings', label: 'Settings' },
          { value: 'configuration', label: 'Configuration' }
        ]}
      />
      <TabsContent
        value="settings"
        forceMount
        className="mt-9 flex flex-col gap-y-9 data-[state=inactive]:hidden"
      >
        <React.Fragment>
          <RichText
            title="General"
            description="Start with setting up your offer mandatory fields"
            className="mb-5"
          />
          <div className="flex flex-col gap-5 mb-2">
            <div className="grid grid-cols-2 gap-5">
              <FormField
                control={form.control}
                name="name"
                render={renderNameField}
              />
              <FormField
                control={form.control}
                name="publisherOfferId"
                render={renderExternalIdField}
              />
            </div>
            <div className="grid grid-cols-2 gap-5">
              <FormField
                control={form.control}
                name="offerUiId"
                render={renderOfferDesignField}
              />
              <FormField
                control={form.control}
                name="priority"
                render={renderPriorityField}
              />
            </div>
            <div className="grid grid-cols-2 gap-5">
              <FormField
                control={form.control}
                name="startTimeZone"
                render={renderTimeZoneField}
              />
              <FormField
                control={form.control}
                name="startTime"
                render={renderStartTimeField}
              />
            </div>
          </div>
        </React.Fragment>
        <Separator className="bg-base-border" />
        <SegmentsSection
          items={form.watch('segments') || []}
          onItemsChange={handleSegmentsChange}
        />
      </TabsContent>
      <TabsContent
        value="configuration"
        forceMount
        className="mt-9 flex flex-col gap-y-5 data-[state=inactive]:hidden"
      >
        <ProductsSection
          form={form as any}
          isSingleProductMode={false}
          showStartOver
          formatProductQuantity={formatProductQuantity}
          products={getProducts}
          productsList={productsList}
          addProduct={addProduct}
          deleteProduct={deleteProduct}
          updateProduct={updateProduct}
        />
      </TabsContent>
    </Tabs>
  );
};

export { RewardCalendarContent };
